import type { Canvas } from "../../Canvas.ts";
import { type Drawable, MAX_SIZE, type Position, type Size, ZERO_SIZE } from "../../Drawable.ts";
import { drawBorderAsset } from "./MetalBeveledBorder.ts";

export type ArtStyleFrame = "screenprint" | "woodcut" | "ink-watercolor";

export const isArtStyleFrame = (value: unknown): value is ArtStyleFrame =>
	value === "screenprint" || value === "woodcut" || value === "ink-watercolor";

const assetPath = (style: ArtStyleFrame, name: string): string =>
	`/images/assets/card-frame/art-styles/${style}/${name}.png`;

export const ART_STYLE_FRAME_ASSETS = {
	screenprint: {
		full: assetPath("screenprint", "full"),
		corner: assetPath("screenprint", "corner"),
		edgeHorizontal: assetPath("screenprint", "edge-horizontal"),
		edgeVertical: assetPath("screenprint", "edge-vertical"),
	},
	woodcut: {
		full: assetPath("woodcut", "full"),
		corner: assetPath("woodcut", "corner"),
		edgeHorizontal: assetPath("woodcut", "edge-horizontal"),
		edgeVertical: assetPath("woodcut", "edge-vertical"),
	},
	"ink-watercolor": {
		full: assetPath("ink-watercolor", "full"),
		corner: assetPath("ink-watercolor", "corner"),
		edgeHorizontal: assetPath("ink-watercolor", "edge-horizontal"),
		edgeVertical: assetPath("ink-watercolor", "edge-vertical"),
	},
} as const;

export const ALL_ART_STYLE_FRAME_ASSETS = Object.values(ART_STYLE_FRAME_ASSETS).flatMap((assets) =>
	Object.values(assets),
);

export class ArtStyleCardFrame implements Drawable {
	constructor(private readonly style: ArtStyleFrame) {}

	public draw(document: Canvas, position: Position, size: Readonly<Size>): void {
		drawBorderAsset(
			document,
			ART_STYLE_FRAME_ASSETS[this.style].full,
			position.x,
			position.y,
			size.width,
			size.height,
		);
	}

	public minSize(): Size {
		return ZERO_SIZE;
	}

	public maxSize(): Size {
		return MAX_SIZE;
	}

	public widthToHeightRatio(): number {
		return 1;
	}
}

const BOARD_COLORS: Record<ArtStyleFrame, { outer: string; inner: string }> = {
	screenprint: { outer: "#df7048", inner: "#f6e7c7" },
	woodcut: { outer: "#af332e", inner: "#f1dfbf" },
	"ink-watercolor": { outer: "#203947", inner: "#759cac" },
};

export class ArtStyleMinimalCardFrame implements Drawable {
	constructor(private readonly style: ArtStyleFrame) {}

	public draw(document: Canvas, position: Position, size: Readonly<Size>): void {
		const unit = Math.min(size.width, size.height);
		const colors = BOARD_COLORS[this.style];
		document.save();
		document
			.lineWidth(unit * 0.032)
			.strokeColor(colors.outer)
			.roundedRect(
				position.x + unit * 0.027,
				position.y + unit * 0.027,
				size.width - unit * 0.054,
				size.height - unit * 0.054,
				this.style === "woodcut" ? 0 : unit * 0.025,
			)
			.stroke();
		document
			.lineWidth(unit * 0.007)
			.strokeColor(colors.inner)
			.roundedRect(
				position.x + unit * 0.061,
				position.y + unit * 0.061,
				size.width - unit * 0.122,
				size.height - unit * 0.122,
				this.style === "woodcut" ? 0 : unit * 0.012,
			)
			.stroke();
		document.restore();
	}

	public minSize(): Size {
		return ZERO_SIZE;
	}

	public maxSize(): Size {
		return MAX_SIZE;
	}

	public widthToHeightRatio(): number {
		return 1;
	}
}
